import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebounce } from 'react-use'
import { allCountries, CountryData, Region, US } from 'country-region-data'

import { AddressAdapter, DEFAULT_ZIP_LENGTH, InputState, INVALID_ZIP } from '@makyoapp/makyo-common'
import { CountryOption, Field, Label, SelectDropdown, SimpleInput } from '@makyoapp/makyo-ui'

import { DEFAULT_DEBOUNCE_MS, STORAGE_KEYS } from '@/shared/constants'

export interface AddressFieldProps {
  id: string
  state: InputState
  setState: (newState: InputState) => void
}

/**
 * get regions name based on country data
 *
 * @param country
 * @param countries
 */
export const getRegions = (country: string, countries: CountryData[]): Region[] => {
  if (!country) {
    return []
  }
  const searchIndex = 0

  let selectedCountry: CountryData | null = null
  countries.forEach((row) => {
    if (row[searchIndex] === country) {
      selectedCountry = row
    }
  })

  // this could happen if the user is managing the state of the region/country themselves and screws up passing
  // in a valid country
  if (!selectedCountry) {
    console.error(
      'Error. Unknown country passed: ' +
        country +
        '. If you\'re passing a country shortcode, be sure to include countryValueType="short" on the RegionSelector'
    )
    return []
  }

  return selectedCountry[2]
}

/**
 * display default country
 */
export function getDefaultCountry() {
  const userData = localStorage.getItem(STORAGE_KEYS.USER_GEO_INFO)
  if (userData) {
    const country = allCountries.find(([, slug]) => slug === userData)
    return country ? country[0] : US[0]
  } else {
    return US[0]
  }
}

/**
 * address form state
 */
export interface AddressFormState {
  title: InputState
  country: InputState
  addressLine1: InputState
  addressLine2?: string
  state: InputState
  city: InputState
  zip: InputState
  phone?: string
}

/**
 * default initial state
 */
/*
const initialAddressFormState: AddressFormState = {
  title: { ...initialInputState },
  country: {
    ...initialInputState,
    value: US[0],
    disableButton: false
  },
  addressLine1: { ...initialInputState },
  addressLine2: '',
  state: {
    ...initialInputState,
    disableButton: false,
    value: getRegions(allCountries[0][0], allCountries)[0][0]
  },
  city: { ...initialInputState },
  zip: { ...initialInputState }
}*/

export function getInitialAddressState(value?: AddressAdapter): AddressFormState {
  return {
    title: {
      error: null,
      value: value?.title || '',
      disableButton: !value?.title,
    },
    addressLine1: {
      error: null,
      value: value?.addressLine1 || '',
      disableButton: !value?.addressLine1,
    },
    addressLine2: value?.addressLine2 || '',
    country: {
      value: value?.country || getDefaultCountry(),
      error: null,
      disableButton: false,
    },
    state: {
      value: value?.state || getRegions(value?.country || getDefaultCountry(), allCountries)[0][0],
      error: null,
      disableButton: false,
    },
    city: {
      error: null,
      value: value?.city || '',
      disableButton: !value?.city,
    },
    zip: {
      error: null,
      value: value?.zip || '',
      disableButton: !value?.zip,
    },
  }
}

/**
 * Postal / Zip code field
 *
 * @param state
 * @param setState
 * @constructor
 */
export function ZipField({ state, setState }: AddressFieldProps) {
  const { t } = useTranslation()
  const [debounceZip, setDebounceZip] = useState('')
  const [, cancelDebounceZip] = useDebounce(
    () => {
      setDebounceZip(state.value)
    },
    DEFAULT_DEBOUNCE_MS,
    [state.value]
  )

  useEffect(() => {
    if (debounceZip) {
      const ec = debounceZip.length < DEFAULT_ZIP_LENGTH && INVALID_ZIP
      if (ec) {
        setState({
          value: debounceZip,
          error: ec,
          disableButton: true,
        })
      } else {
        setState({
          value: debounceZip,
          error: null,
          disableButton: false,
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceZip])

  return (
    <Field>
      <Label required>{t('Zip / Postal code')}</Label>
      <SimpleInput
        className='rtl:[direction:ltr]'
        required
        autoComplete='postal-code'
        data-form-type='address,billing,zip'
        value={state.value}
        error={state.error ? t(state.error) : undefined}
        onChange={(e) => {
          //setIsInitial(false)
          const value = e.currentTarget.value.replace(/[^a-zA-Z0-9\s\-]+/gm, '')
          const ec = value.length < DEFAULT_ZIP_LENGTH && INVALID_ZIP
          if (ec) {
            setState({
              ...state,
              disableButton: true,
              value,
            })
          } else {
            cancelDebounceZip()
            setState({
              disableButton: false,
              error: null,
              value,
            })
          }
        }}
      />
    </Field>
  )
}

/**
 * standard required field
 */
export function RequiredField({
  id,
  state,
  setState,
  label,
  placeholder,
  inputProps,
  className,
}: AddressFieldProps & {
  label: string
  placeholder?: string
  inputProps?: React.ComponentPropsWithoutRef<'input'> & {
    [k: string]: string | number | boolean
  }
  className?: string
}) {
  const { t } = useTranslation()
  const name = state.value
  return (
    <Field>
      <Label required>{label}</Label>
      <SimpleInput
        id={id}
        className={className}
        required
        placeholder={placeholder}
        value={name}
        error={state.error}
        {...inputProps}
        onChange={(e) => {
          const value = e.currentTarget.value
          if (!value) {
            setState({
              value,
              disableButton: true,
              error: t('Field is required'),
            })
          } else {
            setState({
              value,
              disableButton: false,
              error: null,
            })
          }
        }}
      />
    </Field>
  )
}

/**
 * country dropdown field
 */
export function CountryField({ id, state, setState }: AddressFieldProps) {
  const selected = allCountries.find(([countryName]) => countryName.toLowerCase() === state.value.toLowerCase())

  const [selectedVal, setSelected] = useState(selected)
  const { t } = useTranslation()

  return (
    <Field>
      <Label required>{t('Country')}</Label>
      <SelectDropdown
        id={id}
        className='rtl:[direction:ltr]'
        value={selectedVal}
        options={allCountries}
        optionLabel={([countryName]) => countryName}
        withSearch={true}
        enableAutoScroll={true}
        menuClassName='max-w-[340px]'
        onChange={(opt) => {
          const [countryName] = opt
          setSelected(opt)
          setState({ ...state, disableButton: false, value: countryName })
        }}
        searchInputProps={{
          autoComplete: 'country-name',
        }}
        renderOption={([countryName, countrySlug]) => {
          return <CountryOption slug={countrySlug} name={countryName} />
        }}
      />
    </Field>
  )
}

interface StateProvinceFieldProps {
  id: string
  country: string
  state: InputState
  setState: (newState: InputState) => void
}

/**
 * State / Province dropdown field
 *
 * options depend on country props
 *
 * @param props
 * @constructor
 */
export function StateProvinceField(props: StateProvinceFieldProps) {
  const regions = getRegions(props.country, allCountries)

  const selected: Region = props.state.value
    ? regions.find((r) => r[0] === props.state.value) || regions[0]
    : regions[0]

  const [value, setValue] = useState(selected)
  const { t } = useTranslation()

  useEffect(() => {
    setValue(selected)
  }, [selected])

  return (
    <Field>
      <Label required>{t('State / Province')}</Label>
      <SelectDropdown
        id={props.id}
        className='rtl:[direction:ltr]'
        value={value}
        onChange={(opt) => {
          setValue(opt)
          props.setState({
            ...props.state,
            disableButton: false,
            value: opt[0],
          })
        }}
        renderOption={(opt) => {
          return opt[0]
        }}
        options={regions}
      />
    </Field>
  )
}
export interface AddressFormProps {
  id: string
  state: AddressFormState
  setState: (patch: Partial<AddressFormState> | ((prevState: AddressFormState) => Partial<AddressFormState>)) => void
}

/**
 * address form
 */
export function AddressForm({ id, state, setState }: AddressFormProps) {
  const { t } = useTranslation()
  return (
    <React.Fragment>
      <RequiredField
        id={`${id}-title`}
        state={state.title}
        label={t('Title')}
        //className='rtl:[direction:ltr]'
        inputProps={{
          autoComplete: 'name',
          'data-form-type': 'address,billing,address_name',
        }}
        placeholder={t('Please enter title')}
        setState={(title) => setState({ title })}
      />
      <CountryField
        id={`${id}-country`}
        state={state.country}
        setState={(country) => {
          setState({
            country,
            state: {
              ...state.state,
              value: getRegions(country.value, allCountries)[0][0],
            },
          })
        }}
      />
      <RequiredField
        id={`${id}-address1`}
        label={t('Address Line 1')}
        className='rtl:[direction:ltr]'
        inputProps={{
          autoComplete: 'address-line1',
          'data-form-type': 'address,billing,street',
        }}
        state={state.addressLine1}
        setState={(addressLine1) => setState({ addressLine1 })}
      />
      <Field>
        <Label>{t('Address Line 2')}</Label>
        <SimpleInput
          id={`${id}-address2`}
          className='rtl:[direction:ltr]'
          value={state.addressLine2}
          autoComplete='address-line2'
          onChange={(e) =>
            setState({
              addressLine2: e.currentTarget.value,
            })
          }
        />
      </Field>
      <RequiredField
        id={`${id}-city`}
        state={state.city}
        className='rtl:[direction:ltr]'
        inputProps={{
          autoComplete: 'address-level2',
        }}
        setState={(city) => setState({ city })}
        label={t('City')}
      />
      <StateProvinceField
        id={`${id}-state`}
        country={state.country.value}
        state={state.state}
        setState={(stateProv) => setState({ state: stateProv })}
      />
      <ZipField id={`${id}-zip`} state={state.zip} setState={(zip) => setState({ zip })} />
      {/*<Field>
        <Label>{t('Phone Number')}</Label>
        <SimpleInput
          type='tel'
          autoComplete='tel'
          onChange={(e) => {
            const val = e.currentTarget.value
            const valid = validateLuhn(val)
            if (valid) {
              setState({
                phone: val
              })
            }
          }}
        />
      </Field>*/}
    </React.Fragment>
  )
}

/**
 * check whether button should be disabled
 * by checking all required field disable button state
 *
 * @param state
 */
export function isDisableAddressFormButton(state: AddressFormState): boolean {
  return [
    state.title.disableButton,
    state.country.disableButton,
    state.addressLine1.disableButton,
    state.city.disableButton,
    state.state.disableButton,
    state.zip.disableButton,
  ].some((v) => v)
}
